/**
 * Core types for the centralized roles and permissions system.
 *
 * This module defines the foundational types used throughout the
 * permission checking infrastructure. All permission checks flow
 * through these types.
 */

// -------------------------------------------------------
// Roles
// -------------------------------------------------------

export const ROLES = [
  "admin",
  "supervising_pharmacist",
  "pharmacist",
  "pharmacy_intern",
  "technician",
  "auditor",
] as const;

export type Role = (typeof ROLES)[number];

// -------------------------------------------------------
// Drug Schedule Context
// -------------------------------------------------------

export const DRUG_SCHEDULES = [
  "schedule_ii",
  "schedule_iii",
  "schedule_iv",
  "schedule_v",
  "non_controlled",
] as const;

export type DrugSchedule = (typeof DRUG_SCHEDULES)[number];

// -------------------------------------------------------
// Permission Access Levels (3 states)
// -------------------------------------------------------

export type PermissionAccess = "allowed" | "denied" | "requires_approval";

// -------------------------------------------------------
// Permission Context (metadata for approval tokens)
// -------------------------------------------------------

export type PermissionContext = Record<string, never>;

// -------------------------------------------------------
// Permission Check Results (discriminated union)
// -------------------------------------------------------

export interface PermissionAllowed {
  type: "allowed";
}

export interface PermissionDenied {
  type: "denied";
  reason: string;
}

export interface PermissionRequiresApproval {
  type: "requires_approval";
  reason: string;
  approverRoles: Role[];
}

export type PermissionResult =
  | PermissionAllowed
  | PermissionDenied
  | PermissionRequiresApproval;

// -------------------------------------------------------
// Permission Override (for future per-pharmacy customization)
// -------------------------------------------------------

export interface PermissionOverride {
  role: Role;
  permission: string;
  access: PermissionAccess;
}
